/**
 * Astrixy Consent Manager — framework-agnostic consent engine.
 *
 * Responsibilities:
 *   - Persist and version consent decisions (policy + consent version).
 *   - Enforce expiry and re-prompt.
 *   - Honour Global Privacy Control (GPC) and Do Not Track (DNT).
 *   - Broadcast state changes so tag loaders can react without a reload.
 *   - Purge cookies / localStorage / sessionStorage on withdrawal.
 *
 * The manager NEVER loads a vendor script itself; `loaders.ts` subscribes
 * to it. That separation is what guarantees "nothing before consent".
 */

import {
  CONSENT_TTL_DAYS,
  CONSENT_VERSION,
  DENIED_STATE,
  GRANTED_STATE,
  LEGACY_STORAGE_KEY,
  POLICY_VERSION,
  STORAGE_KEY,
  type ConsentCategory,
  type ConsentMethod,
  type ConsentRecord,
  type ConsentState,
} from "./types";

type Listener = (record: ConsentRecord | null) => void;

const listeners = new Set<Listener>();
let current: ConsentRecord | null = null;
let bootstrapped = false;

const isBrowser = () => typeof window !== "undefined";

// ── Environment signals ─────────────────────────────────
export const hasGPC = (): boolean =>
  isBrowser() && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

export const hasDNT = (): boolean => {
  if (!isBrowser()) return false;
  const nav = navigator as Navigator & { msDoNotTrack?: string };
  const win = window as Window & { doNotTrack?: string };
  const raw = nav.doNotTrack ?? win.doNotTrack ?? nav.msDoNotTrack;
  return raw === "1" || raw === "yes";
};

const deviceClass = (): ConsentRecord["device"] => {
  if (!isBrowser()) return "desktop";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
};

const newConsentId = (): string => {
  if (isBrowser() && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

// ── Persistence ─────────────────────────────────────────
const read = (): ConsentRecord | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (!parsed?.categories || typeof parsed.consentVersion !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
};

const write = (record: ConsentRecord) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* private mode / quota — consent stays session-only */
  }
};

/** A stored record is only usable if it is current *and* unexpired. */
export const isRecordValid = (record: ConsentRecord | null): record is ConsentRecord => {
  if (!record) return false;
  if (record.consentVersion !== CONSENT_VERSION) return false;
  if (record.policyVersion !== POLICY_VERSION) return false;
  if (!record.expiresAt || Date.parse(record.expiresAt) <= Date.now()) return false;
  return true;
};

// ── Purge ───────────────────────────────────────────────
const TRACKER_COOKIE_PREFIXES = [
  "_ga", "_gid", "_gat", "_gcl", "_dc_gtm", "AMP_TOKEN", "IDE", "test_cookie",
  "_fbp", "_fbc", "fr", "_ttp", "_tt_enable_cookie", "li_sugr", "bcookie",
  "lidc", "UserMatchHistory", "AnalyticsSyncHistory", "YSC", "VISITOR_INFO1_LIVE",
  "ph_", "posthog",
];


const TRACKER_STORAGE_PREFIXES = [
  "ph_", "posthog", "_ga", "fbq", "ttq", "__tt", "amplitude", "mp_", "ajs_",
  "astrixy_attribution", "astrixy_pixel_map", "astrixy_reco",
  "astrixy_recently_viewed", "astrixy_experiments",
];

const cookieDomains = (): string[] => {
  const host = window.location.hostname;
  const out = new Set<string>([host, `.${host}`]);
  const parts = host.split(".");
  for (let i = 1; i < parts.length - 1; i++) out.add(`.${parts.slice(i).join(".")}`);
  return [...out];
};

/** Deletes every known non-essential cookie across all plausible scopes. */
export const purgeTrackingCookies = () => {
  if (!isBrowser()) return;
  const domains = cookieDomains();
  document.cookie.split(";").forEach((raw) => {
    const name = raw.split("=")[0]?.trim();
    if (!name) return;
    if (!TRACKER_COOKIE_PREFIXES.some((p) => name.startsWith(p))) return;
    const expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = `${name}=; ${expiry}; path=/`;
    domains.forEach((d) => {
      document.cookie = `${name}=; ${expiry}; path=/; domain=${d}`;
    });
  });
};

/** Removes tracker-owned localStorage / sessionStorage keys. */
export const purgeTrackingStorage = () => {
  if (!isBrowser()) return;
  ([window.localStorage, window.sessionStorage] as Storage[]).forEach((store) => {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (!key || key === STORAGE_KEY) continue;
        if (TRACKER_STORAGE_PREFIXES.some((p) => key.startsWith(p))) doomed.push(key);
      }
      doomed.forEach((k) => store.removeItem(k));
    } catch {
      /* ignore */
    }
  });
};

// ── Google Consent Mode v2 ──────────────────────────────
const gcmState = (c: ConsentState) => ({
  ad_storage: c.marketing ? "granted" : "denied",
  ad_user_data: c.marketing ? "granted" : "denied",
  ad_personalization: c.marketing ? "granted" : "denied",
  analytics_storage: c.analytics ? "granted" : "denied",
  personalization_storage: c.personalization ? "granted" : "denied",
  functionality_storage: c.functional ? "granted" : "denied",
  security_storage: "granted",
});

const pushConsentMode = (record: ConsentRecord) => {
  if (!isBrowser()) return;
  const w = window as unknown as {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
  }
  const state = gcmState(record.categories);
  w.gtag("consent", "update", state);
  w.gtag("set", "ads_data_redaction", !record.categories.marketing);
  w.gtag("set", "url_passthrough", true);
  w.dataLayer.push({
    event: "astrixy_consent_update",
    consent_id: record.consentId,
    consent_version: record.consentVersion,
    policy_version: record.policyVersion,
    consent_method: record.method,
    ...state,
  });
};

// ── Record construction ─────────────────────────────────
const buildRecord = (categories: ConsentState, method: ConsentMethod): ConsentRecord => {
  const now = new Date();
  return {
    consentId: current?.consentId ?? newConsentId(),
    categories: { ...categories, necessary: true },
    consentVersion: CONSENT_VERSION,
    policyVersion: POLICY_VERSION,
    method,
    timestamp: now.toISOString(),
    expiresAt: new Date(now.getTime() + CONSENT_TTL_DAYS * 864e5).toISOString(),
    signals: { gpc: hasGPC(), dnt: hasDNT() },
    language: isBrowser() ? navigator.language : "en",
    timeZone: isBrowser() ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC",
    device: deviceClass(),
  };
};

const emit = () => listeners.forEach((l) => l(current));

// ── Public API ──────────────────────────────────────────

export const getRecord = (): ConsentRecord | null => current;

export const getConsent = (): ConsentState => current?.categories ?? { ...DENIED_STATE };

export const isAllowed = (category: ConsentCategory): boolean =>
  category === "necessary" ? true : getConsent()[category] === true;

/** True when the banner must be shown (no valid, current, unexpired record). */
export const needsPrompt = (): boolean => !isRecordValid(current);

export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
};

/** Runs `cb` immediately if the category is granted, and on any later grant. */
export const onGrant = (category: ConsentCategory, cb: () => void): (() => void) => {
  let fired = false;
  return subscribe(() => {
    if (!fired && isAllowed(category)) {
      fired = true;
      cb();
    }
  });
};

export interface SetConsentOptions {
  /** Skip writing to the remote audit log (used for bootstrap replay). */
  silent?: boolean;
}

let auditSink: ((record: ConsentRecord) => void) | null = null;
export const setAuditSink = (fn: (record: ConsentRecord) => void) => {
  auditSink = fn;
};

export const setConsent = (
  categories: Partial<ConsentState>,
  method: ConsentMethod,
  options: SetConsentOptions = {},
): ConsentRecord => {
  const merged: ConsentState = { ...getConsent(), ...categories, necessary: true };
  const previous = current;
  const record = buildRecord(merged, method);
  current = record;
  write(record);
  pushConsentMode(record);

  // Anything revoked in this transition must have its data removed now.
  const revoked = (Object.keys(merged) as ConsentCategory[]).some(
    (k) => previous?.categories[k] && !merged[k],
  );
  if (revoked || (!merged.analytics && !merged.marketing)) {
    disableTrackers(merged);
  }

  emit();
  if (!options.silent) auditSink?.(record);
  return record;
};

export const acceptAll = () => setConsent(GRANTED_STATE, "accept_all");
export const rejectAll = () => setConsent(DENIED_STATE, "reject_all");
export const saveCustom = (categories: Partial<ConsentState>) =>
  setConsent(categories, "custom");

/** Full withdrawal: deny everything, purge storage, force re-prompt. */
export const withdrawConsent = () => {
  setConsent(DENIED_STATE, "withdrawn");
  purgeTrackingCookies();
  purgeTrackingStorage();
  current = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  emit();
};

/** Turns vendor SDKs off in-page and purges their storage. */
function disableTrackers(state: ConsentState) {
  if (!isBrowser()) return;
  const w = window as unknown as Record<string, unknown>;
  if (!state.analytics) {
    w["ga-disable-G-7L4BJHB8FV"] = true;
    try {
      (w.posthog as { opt_out_capturing?: () => void } | undefined)?.opt_out_capturing?.();
    } catch {
      /* ignore */
    }
  }
  if (!state.marketing) {
    try {
      (w.ttq as { disableCookie?: () => void } | undefined)?.disableCookie?.();
    } catch {
      /* ignore */
    }
  }
  purgeTrackingCookies();
  purgeTrackingStorage();
}

/**
 * Boots the manager. Must run before any tag loader.
 * Order of precedence: valid stored record → GPC/DNT auto-deny → prompt.
 */
export const bootstrapConsent = (): ConsentRecord | null => {
  if (bootstrapped || !isBrowser()) return current;
  bootstrapped = true;

  const stored: ConsentRecord | null = read();
  if (isRecordValid(stored)) {
    current = stored;
    pushConsentMode(stored);
    emit();
    return current;
  }

  // Stale/expired/outdated record: keep the consentId for audit continuity.
  const stale = stored as ConsentRecord | null;
  if (stale?.consentId) current = { ...stale, categories: { ...DENIED_STATE } };


  // Legacy v1 preferences are intentionally NOT auto-upgraded to a grant —
  // the category set changed, so we re-ask, but we clean the old key.
  try {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }

  if (hasGPC() || hasDNT()) {
    // Legally binding opt-out signal: record a denial and never prompt for
    // tracking categories again in this browser.
    const record = buildRecord(DENIED_STATE, hasGPC() ? "gpc_signal" : "dnt_signal");
    current = record;
    write(record);
    pushConsentMode(record);
    disableTrackers(DENIED_STATE);
    emit();
    auditSink?.(record);
    return current;
  }

  current = null;
  emit();
  return null;
};
